import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { getPaginatedEvents } from '../../api/eventsAPI';
import { getAssetDashboardSummary, getPaginatedAssets } from '../../api/assetAPI';
import { getAssetHierarchy } from '../../api/asset-metrics-dashboardAPI';
import { getHierarchyStructures } from '../../api/hierarchy-structureAPI';
import { getSettings } from '../../api/settingsAPI';
import { setApiKey, setMetricsApiBase } from 'asset-metrics-dashboard';
import { getSummaryMetrics, fetchUtilisation } from 'asset-metrics-dashboard';
import assetMetricsCmmsDataProviderByAssetIds from '../../dataProviders/assetMetricsCmmsDataProviderByAssetIds';
import { formatIntDateTime, parseUtc } from '../../utils/dateUtils';
import { useSetting } from '../../utils/AppSettingsContext';
import { useModule } from '../../utils/useModule';
import { useHasPageAccess } from '../../masterLayout/MasterLayoutBody';

const getDownHours = (startDate, now) => {
  const start = parseUtc(startDate);
  if (Number.isNaN(start.getTime())) return '—';
  const diffMs = now.getTime() - start.getTime();
  if (diffMs <= 0) return '0.00';
  const hours = diffMs / 3_600_000;
  return hours.toFixed(1);
};

const formatDateTimeOrDash = (value) => {
  const formatted = formatIntDateTime(value);
  return formatted || '—';
};

const isFutureAssetStartDate = (assetStartDate, now) => {
  const startDate = parseUtc(assetStartDate);
  if (Number.isNaN(startDate.getTime())) return false;
  return startDate > now;
};

const placeholder = `${process.env.REACT_APP_SPACES_BASE}/placeholder.png`;
const RADIAL_RADIUS = 35;
const RADIAL_CIRCUMFERENCE = 2 * Math.PI * RADIAL_RADIUS;
const STATUS_CONFIG = {
  up: {
    label: 'Up',
    color: 'success',
    icon: '',
  },
  down: {
    label: 'Down',
    color: 'danger',
    icon: '',
  },
  standby: {
    label: 'Standby',
    color: 'secondary',
    icon: '',
  },
};
const STATUS_CARD_CONFIG = {
  up: {
    label: 'Up',
    cardClass: 'bg-success-focus',
    circleClass: 'bg-success',
  },
  down: {
    label: 'Down',
    cardClass: 'bg-danger-focus',
    circleClass: 'bg-danger',
  },
  standby: {
    label: 'Standby',
    cardClass: 'bg-primary-focus',
    circleClass: 'bg-primary',
  },
};
const STATUS_ROW_CLASS = {
  up: '', // bg-success-focus
  down: 'bg-danger-focus',
  standby: 'bg-primary-focus',
};

const emptyPerformanceSettings = { departments: [], kpis: [] };

const parseSettingsValue = (rawValue) => {
  if (!rawValue) return emptyPerformanceSettings;
  if (typeof rawValue === 'object') {
    return {
      departments: Array.isArray(rawValue.departments) ? rawValue.departments : [],
      kpis: Array.isArray(rawValue.kpis) ? rawValue.kpis : [],
    };
  }

  if (typeof rawValue !== 'string') return emptyPerformanceSettings;

  const trimmed = rawValue.trim();
  if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
    try {
      const parsed = JSON.parse(trimmed);
      return {
        departments: Array.isArray(parsed.departments) ? parsed.departments : [],
        kpis: Array.isArray(parsed.kpis) ? parsed.kpis : [],
      };
    } catch {
      return emptyPerformanceSettings;
    }
  }

  const jsonStart = trimmed.indexOf('{');
  const jsonEnd = trimmed.lastIndexOf('}');
  if (jsonStart === -1 || jsonEnd === -1 || jsonEnd <= jsonStart) {
    return emptyPerformanceSettings;
  }

  const jsonString = trimmed.slice(jsonStart, jsonEnd + 1).replace(/\\"/g, '"');
  try {
    const parsed = JSON.parse(jsonString);
    return {
      departments: Array.isArray(parsed.departments) ? parsed.departments : [],
      kpis: Array.isArray(parsed.kpis) ? parsed.kpis : [],
    };
  } catch {
    return emptyPerformanceSettings;
  }
};

const formatNumber = (value, digits = 1) => {
  if (!Number.isFinite(value)) return '—';
  return value.toFixed(digits);
};

const calculatePercent = (value, total) => {
  if (!Number.isFinite(value) || !Number.isFinite(total) || total <= 0) return 0;
  return Math.round((value / total) * 100);
};

const paginateRows = (rows, pageSize) => {
  if (!rows.length || pageSize <= 0) return [];
  const pages = [];
  for (let i = 0; i < rows.length; i += pageSize) {
    pages.push(rows.slice(i, i + pageSize));
  }
  return pages;
};

const getPreviousMonthRange = () => {
  const now = new Date();
  const start = new Date(now);
  start.setDate(start.getDate() - 30);
  const end = now;
  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10),
  };
};

const PerformanceDashboardLayer = ({ onTitleChange, isPaused }) => {
  const hasPageAccess = useHasPageAccess();
  const canAccess = hasPageAccess('/dashboards/performance');
  const [events, setEvents] = useState([]);
  const [pendingEvents, setPendingEvents] = useState([]);
  const [assets, setAssets] = useState([]);
  const [assetKpis, setAssetKpis] = useState([]);
  const [departmentSummary, setDepartmentSummary] = useState({});
  const [dashboardSettings, setDashboardSettings] = useState(emptyPerformanceSettings);
  const [loading, setLoading] = useState(true);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [assetLoading, setAssetLoading] = useState(true);
  const [kpiLoading, setKpiLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metricsError, setMetricsError] = useState(null);
  const [timestamp, setTimestamp] = useState(() => new Date());
  const [screenIndex, setScreenIndex] = useState(2);
  const [statusPageIndex, setStatusPageIndex] = useState(0);
  const [downtimeDepartmentIndex, setDowntimeDepartmentIndex] = useState(0);
  const [downtimePageIndex, setDowntimePageIndex] = useState(0);
  const [utilisationDepartmentIndex, setUtilisationDepartmentIndex] = useState(0);
  const [utilisationPageIndex, setUtilisationPageIndex] = useState(0);
  const [assetCardPageIndex, setAssetCardPageIndex] = useState(0);
  const [plannedPageIndex, setPlannedPageIndex] = useState(0);
  const initialKpiLoadedRef = useRef(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [assetSummary, setAssetSummary] = useState({
    totalAssets: 0,
    onHire: 0,
    offHire: 0,
    totalDownCount: 0,
  });
  const PAGE_SIZE = 500;
  const refreshSeconds = useSetting('dashboard_refresh_seconds') ?? 60;
  const REFRESH_INTERVAL_MS = (Number(refreshSeconds) || 60) * 1000;
  const maxPageRecordsSetting = useSetting('dashboard_records_per_page');
  const planthireEnabled = useModule('planthire');
  const metricsApiBase = process.env.REACT_APP_LCCENGINE_API_BASE || '';
  const metricsApiKey = process.env.REACT_APP_LCCENGINE_API_KEY || '';

  useEffect(() => {
    if (metricsApiBase) {
      setMetricsApiBase(metricsApiBase);
    }
    if (metricsApiKey) {
      setApiKey(metricsApiKey);
    }
  }, [metricsApiBase, metricsApiKey]);

  const maxPageRecords = useMemo(() => {
    const parsed = Number(maxPageRecordsSetting);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }

    return PAGE_SIZE;
  }, [maxPageRecordsSetting]);

  const eligibleAssets = useMemo(() => {
    return assets.filter((asset) => {
      return !isFutureAssetStartDate(asset?.assetStartDate, timestamp);
    });
  }, [assets, timestamp]);

  const assetStartDateById = useMemo(() => {
    return new Map(
      assets
        .filter((asset) => asset?.id)
        .map((asset) => [String(asset.id), asset?.assetStartDate])
    );
  }, [assets]);

  const eligibleAssetIds = useMemo(() => {
    return new Set(eligibleAssets.map((asset) => String(asset?.id)));
  }, [eligibleAssets]);

  const dashboardDepartments = useMemo(() => {
    const settingsDepartments = Array.isArray(dashboardSettings.departments)
      ? dashboardSettings.departments
      : [];
    if (settingsDepartments.length) return settingsDepartments;

    const fallbackDepartments = eligibleAssets
      .map((asset) => asset?.assetDepartment?.name)
      .filter(Boolean);
    return Array.from(new Set(fallbackDepartments));
  }, [dashboardSettings.departments, eligibleAssets]);

  const departmentLookup = useMemo(() => {
    return new Set(
      dashboardDepartments.map((department) => String(department).trim().toLowerCase())
    );
  }, [dashboardDepartments, eligibleAssetIds]);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getPaginatedEvents({
        page: 1,
        pageSize: PAGE_SIZE,
        eventStatusId: [1],
        sortField: 'startDate',
        sortOrder: 'asc',
      });
      const rows = Array.isArray(response?.data) ? response.data : [];
      setEvents(rows);
    } catch (err) {
      console.error('Failed to load downtime events', err);
      setError('Unable to load downtime events right now.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadEventsPending = useCallback(async () => {
    setPendingLoading(true);
    setError(null);
    try {
      const response = await getPaginatedEvents({
        page: 1,
        pageSize: PAGE_SIZE,
        eventStatusId: [4],
        sortField: 'plannedStartDate',
        sortOrder: 'asc',
      });
      const rows = Array.isArray(response?.data) ? response.data : [];
      setPendingEvents(rows);
    } catch (err) {
      console.error('Failed to load pending events', err);
      setError('Unable to load planned events right now.');
      setPendingEvents([]);
    } finally {
      setPendingLoading(false);
    }
  }, []);

  const loadAssets = useCallback(async () => {
    setAssetLoading(true);
    try {
      const response = await getPaginatedAssets({
        page: 1,
        pageSize: 1000,
      });
      const rows = Array.isArray(response) ? response : response?.data || [];

      setAssets(rows);
    } catch (err) {
      console.error('Failed to load assets', err);
      setAssets([]);
    } finally {
      setAssetLoading(false);
    }
  }, []);

  const loadDashboardSettings = useCallback(async () => {
    try {
      const response = await getSettings('performance_dashboard_settings');
      const settingsRows = Array.isArray(response) ? response : response?.data || [];
      const setting = settingsRows.find((item) => item?.name === 'performance_dashboard_settings');
      setDashboardSettings(parseSettingsValue(setting?.value ?? setting ?? response));
    } catch (err) {
      console.error('Failed to load performance dashboard settings', err);
      setDashboardSettings(emptyPerformanceSettings);
    }
  }, []);

  const buildMetricLookup = (metrics = []) => {
    return metrics.reduce((acc, metric) => {
      if (metric?.metricId) {
        acc[metric.metricId] = metric;
      }
      return acc;
    }, {});
  };

  const resolveCalendarHours = (usageValue, calendarUtilisationValue) => {
    const usage = Number(usageValue);
    const calendarUtil = Number(calendarUtilisationValue);
    if (!Number.isFinite(usage) || !Number.isFinite(calendarUtil) || calendarUtil <= 0) {
      return null;
    }
    return usage / (calendarUtil / 100);
  };

  const loadAssetKpis = useCallback(async () => {
    setKpiLoading(true);
    try {
      const { start, end } = getPreviousMonthRange();
      const [hierarchyStructures, assetHierarchy] = await Promise.all([
        getHierarchyStructures(),
        getAssetHierarchy(),
      ]);
      const departmentByName = new Map(
        (hierarchyStructures || []).map((department) => [
          String(department?.name || '').trim().toLowerCase(),
          department,
        ])
      );

      const summaryByDepartment = {};
      const rows = [];

      for (const departmentName of dashboardDepartments) {
        const normalized = String(departmentName || '').trim().toLowerCase();
        const department = departmentByName.get(normalized);
        if (!department) continue;

        const departmentAssets = (assetHierarchy?.assets || []).filter((asset) => {
          const assetDeptName = asset?.department;
          if (!assetDeptName) return false;
          return assetDeptName.trim().toLowerCase() === String(departmentName || "").trim().toLowerCase();
        });

        const departmentAssetIds = departmentAssets
          .map((asset) => String(asset.id))
          .filter((assetId) => {
            if (!eligibleAssetIds.size) return true;
            return eligibleAssetIds.has(assetId);
          });

        if (!departmentAssetIds.length) continue;

        const dataProvider = assetMetricsCmmsDataProviderByAssetIds(departmentAssetIds);
        // const meterOptions = await dataProvider.getAvailableMeters();
        const meterOptions = [{ "id": "1", "unit": "HR" }];

        if (!meterOptions?.length) continue;
        const selectedMeter = meterOptions[0];
        const baseFilters = {
          assetIds: departmentAssetIds,
          dateFrom: start,
          dateTo: end,
        };

        const utilData = await fetchUtilisation(
          baseFilters,
          selectedMeter,
          dataProvider.loadAssetsForFilters,
        );

        const departmentSummary = await getSummaryMetrics(
          baseFilters,
          selectedMeter,
          false,
          utilData,
          dataProvider
        );

        summaryByDepartment[departmentName] = buildMetricLookup(
          departmentSummary?.metrics || []
        );

        const assetsInDepartment = await dataProvider.getAssets();

        const assetRows = await Promise.all(
          (assetsInDepartment || []).map(async (asset) => {
            const assetDataProvider = assetMetricsCmmsDataProviderByAssetIds([asset.id]);
            const assetMeters = await assetDataProvider.getAvailableMeters();
            const assetMeter =
              assetMeters?.find((meter) => meter.id === selectedMeter.id) ??
              assetMeters?.[0] ??
              selectedMeter;
            if (!assetMeter) return null;

            const assetFilters = {
              assetIds: [String(asset.id)],
              dateFrom: start,
              dateTo: end,
            };

            const assetUtilData = await fetchUtilisation(
              assetFilters,
              assetMeter,
              assetDataProvider.loadAssetsForFilters,
            );

            const assetSummary = await getSummaryMetrics(
              assetFilters,
              assetMeter,
              false,
              assetUtilData,
              assetDataProvider
            );

            const metricLookup = buildMetricLookup(assetSummary?.metrics || []);
            const usage = metricLookup.utilisation?.value ?? null;
            const calendarUtilisation = metricLookup.calendarUtilisation?.value ?? null;
            const calendarHours = resolveCalendarHours(usage, calendarUtilisation);
            const assetLabelParts = [
              asset?.assetName,
              asset?.assetDescription,
            ]
              .filter(Boolean)
              .join(' - ');

            return {
              assetId: asset.id,
              asset: assetLabelParts || asset?.assetName || asset.id,
              assetDescription: asset?.assetDescription,
              assetImage: asset?.assetImage,
              assetDepartment: departmentName,
              usage,
              calendarHours,
              calendarUtilisation,
              effectiveUtilisation: metricLookup.effectiveUtilisation?.value ?? null,
              mechanicalAvailability: metricLookup.mechanicalAvailability?.value ?? null,
              calendarAvailability: metricLookup.calendarAvailability?.value ?? null,
              countAllEvents: metricLookup.stoppageCountTotal?.value ?? 0,
              countBreakdownEvents: metricLookup.stoppageCountBreakdown?.value ?? 0,
              mtbf: metricLookup.meanTimeBetweenFailures?.value ?? null,
              mttr: metricLookup.meanTimeToRepair?.value ?? null,
            };
          })
        );

        rows.push(...assetRows.filter(Boolean));
      }

      setDepartmentSummary(summaryByDepartment);
      setAssetKpis(rows);
    } catch (err) {
      console.error('Failed to load asset KPIs', err);
      setAssetKpis([]);
      setDepartmentSummary({});
    } finally {
      setKpiLoading(false);
    }
  }, [dashboardDepartments, eligibleAssetIds]);

  const loadAssetSummary = useCallback(async () => {
    try {
      setMetricsError(null);
      const response = await getAssetDashboardSummary();
      if (response && typeof response === 'object') {
        setAssetSummary({
          totalAssets: Number(response.totalAssets) || 0,
          onHire: Number(response.onHire) || 0,
          offHire: Number(response.offHire) || 0,
          totalDownCount: Number(response.totalDownCount) || 0,
          outOfServiceAssets: Number(response.outOfServiceAssets) || 0,
          offSiteAssets: Number(response.offSiteAssets) || 0,
        });
      } else {
        throw new Error('Unexpected response when loading asset summary');
      }
    } catch (err) {
      console.error('Failed to load asset dashboard summary', err);
      setMetricsError('Unable to load asset totals right now.');
    }
  }, []);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    loadEventsPending();
  }, [loadEventsPending]);

  useEffect(() => {
    let isMounted = true;
    const loadInitialData = async () => {
      await Promise.all([loadAssets(), loadDashboardSettings()]);
      if (isMounted) {
        setInitialDataLoaded(true);
      }
    };

    loadInitialData();

    return () => {
      isMounted = false;
    };
  }, [loadAssets, loadDashboardSettings]);

  useEffect(() => {
    if (!initialDataLoaded || initialKpiLoadedRef.current) return;
    initialKpiLoadedRef.current = true;
    loadAssetKpis();
  }, [initialDataLoaded, loadAssetKpis]);

  useEffect(() => {
    loadAssetSummary();
  }, [loadAssetSummary]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadEvents();
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [loadEvents, REFRESH_INTERVAL_MS]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadEventsPending();
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [loadEventsPending, REFRESH_INTERVAL_MS]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadAssets();
      loadDashboardSettings();
      loadAssetKpis();
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [loadAssets, loadDashboardSettings, loadAssetKpis, REFRESH_INTERVAL_MS]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadAssetSummary();
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [loadAssetSummary, REFRESH_INTERVAL_MS]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimestamp(new Date());
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [REFRESH_INTERVAL_MS]);

  const eventRows = useMemo(() => {
    return events
      .filter((event) => {
        const assetId = event?.asset?.id;
        const assetStartDate =
          event?.asset?.assetStartDate ??
          (assetId ? assetStartDateById.get(String(assetId)) : null);
        if (!assetStartDate) return true;
        return !isFutureAssetStartDate(assetStartDate, timestamp);
      })
      .map((event) => {
        const assetParts = [];
        if (event?.asset?.assetNumber) assetParts.push(event.asset.assetNumber);
        if (event?.asset?.assetDescription) assetParts.push(event.asset.assetDescription);
        const assetLabel = assetParts.join(' - ') || '—';
        const badgeColor = event?.eventType?.iconColor || 'secondary-600';
        const eventTypeName = event?.eventType?.name || '—';
        return {
          id: event.id,
          assetId: event?.asset?.id,
          asset: assetLabel,
          assetImage: event?.asset?.assetImage || null,
          timeDown: formatDateTimeOrDash(event?.startDate),
          estimatedUp: formatDateTimeOrDash(event?.plannedEndDate),
          downHours: getDownHours(event?.startDate, timestamp),
          reason: event?.name || '—',
          badgeColor,
          eventTypeName,
          assetDepartment: event.asset?.assetDepartment?.name,
          customer: event.asset?.customer?.name,
          rawEvent: event,
        };
      });
  }, [assetStartDateById, events, timestamp]);

  const plannedEventRows = useMemo(() => {
    return pendingEvents.map((event) => {
      const assetNumber = event?.asset?.assetNumber || '—';
      const assetDescription = event?.asset?.assetDescription || '';
      const assetLabelParts = [assetNumber, assetDescription].filter(Boolean).join(' - ');
      const plannedStart = parseUtc(event?.plannedStartDate);
      const plannedEnd = parseUtc(event?.plannedEndDate);
      const hasValidDates =
        !Number.isNaN(plannedStart.getTime()) && !Number.isNaN(plannedEnd.getTime());
      const durationHours = hasValidDates
        ? Math.max(0, (plannedEnd.getTime() - plannedStart.getTime()) / 3_600_000)
        : null;

      return {
        id: event?.id,
        assetNumber,
        assetDescription,
        assetLabel: assetLabelParts || '—',
        assetImage: event?.asset?.assetImage || null,
        departmentName: event?.asset?.assetDepartment?.name || '—',
        plannedStartDate: formatDateTimeOrDash(event?.plannedStartDate),
        plannedEndDate: formatDateTimeOrDash(event?.plannedEndDate),
        durationHours,
        locationName: event?.location?.name || '—',
      };
    });
  }, [pendingEvents]);

  const eventsByAssetId = useMemo(() => {
    const map = new Map();
    eventRows.forEach((row) => {
      if (!row.assetId) return;
      if (!map.has(row.assetId)) {
        map.set(row.assetId, row);
      }
    });
    return map;
  }, [eventRows]);

  const statusRows = useMemo(() => {
    const filteredAssets = eligibleAssets.filter((asset) => {
      if (!departmentLookup.size) return true;
      const departmentName = String(asset?.assetDepartment?.name || '').trim().toLowerCase();
      return departmentLookup.has(departmentName);
    });

    return filteredAssets.map((asset) => {
      const eventRow = asset?.id ? eventsByAssetId.get(asset.id) : null;

      const eventTypeName = eventRow?.eventTypeName?.toLowerCase();
      const statusKey = !eventRow
        ? 'up'
        : eventTypeName === 'standby'
          ? 'standby'
          : 'down';


      return {
        id: asset?.id,
        asset: asset?.assetNumber
          ? `${asset.assetNumber} - ${asset.assetDescription}`
          : asset?.assetNumber || asset?.assetDescription || '—',
        assetImage: asset?.assetImage || eventRow?.assetImage || null,
        assetNumber: asset?.assetNumber || null,
        assetDepartment: asset?.assetDepartment?.name || eventRow?.assetDepartment,
        customer: asset?.customer?.name || eventRow?.customer,
        timeDown: eventRow?.timeDown || '—',
        estimatedUp: eventRow?.estimatedUp || '—',
        downHours: eventRow?.downHours || '—',
        reason: eventRow?.reason || '—',
        eventTypeName: eventRow?.eventTypeName || STATUS_CONFIG[statusKey].label,
        badgeColor: eventRow?.badgeColor || 'secondary-600',
        statusKey,
      };
    });
  }, [departmentLookup, eligibleAssets, eventsByAssetId]);

  const plannedCount = useMemo(() => {
    return eventRows.reduce((count, row) => {
      if (typeof row.eventTypeName !== 'string') return count;
      return row.eventTypeName.toLowerCase() === 'planned' ? count + 1 : count;
    }, 0);
  }, [eventRows]);

  const breakdownCount = useMemo(() => {
    return eventRows.reduce((count, row) => {
      if (typeof row.eventTypeName !== 'string') return count;
      return row.eventTypeName.toLowerCase() === 'breakdown' ? count + 1 : count;
    }, 0);
  }, [eventRows]);

  const standbyCount = useMemo(() => {
    return eventRows.reduce((count, row) => {
      if (typeof row.eventTypeName !== 'string') return count;
      return row.eventTypeName.toLowerCase() === 'standby' ? count + 1 : count;
    }, 0);
  }, [eventRows]);

  const overdueCount = useMemo(() => {
    return events.reduce((count, event) => {
      const plannedEnd = parseUtc(event?.plannedEndDate);
      if (Number.isNaN(plannedEnd.getTime())) return count;
      return plannedEnd < timestamp ? count + 1 : count;
    }, 0);
  }, [events, timestamp]);

  const totalDownCount = eventRows.length - standbyCount;

  const nonHire = assetSummary.totalAssets - assetSummary.onHire - assetSummary.offHire;
  const inServiceAssets = assetSummary.totalAssets - assetSummary.outOfServiceAssets - totalDownCount;

  let percentOnhire = 0;
  let percentOffhire = 0;
  let percentNonhire = 0;
  let percentDown = 0;
  let percentPlanned = 0;
  let percentBreakdown = 0;
  let percentOverdue = 0;
  let percentInservice = 0;
  let percentStandby = 0;

  if (assetSummary.totalAssets) { percentOnhire = parseInt((assetSummary.onHire / (assetSummary.totalAssets)) * 100, 10) };
  if (assetSummary.totalAssets) { percentOffhire = parseInt((assetSummary.offHire / (assetSummary.totalAssets)) * 100, 10) };
  if (assetSummary.totalAssets) { percentNonhire = parseInt((nonHire / (assetSummary.totalAssets)) * 100, 10) };
  if (assetSummary.totalAssets) { percentDown = parseInt((totalDownCount / assetSummary.totalAssets) * 100, 10) };
  if (totalDownCount) { percentPlanned = parseInt((plannedCount / (totalDownCount)) * 100, 10) };
  if (totalDownCount) { percentBreakdown = parseInt((breakdownCount / (totalDownCount)) * 100, 10) };
  if (totalDownCount) { percentOverdue = parseInt((overdueCount / (totalDownCount)) * 100, 10) };
  if (assetSummary.totalAssets) { percentStandby = parseInt((standbyCount / (assetSummary.totalAssets)) * 100, 10) };
  if (assetSummary.totalAssets) { percentInservice = parseInt((inServiceAssets / (assetSummary.totalAssets)) * 100, 10) };

  const assetStrokeOffset = RADIAL_CIRCUMFERENCE - 1 * RADIAL_CIRCUMFERENCE;
  const downStrokeOffset = RADIAL_CIRCUMFERENCE - (percentDown / 100) * RADIAL_CIRCUMFERENCE;
  const plannedStrokeOffset = RADIAL_CIRCUMFERENCE - (percentPlanned / 100) * RADIAL_CIRCUMFERENCE;
  const breakStrokeOffset = RADIAL_CIRCUMFERENCE - (percentBreakdown / 100) * RADIAL_CIRCUMFERENCE;
  const standbyStrokeOffset = RADIAL_CIRCUMFERENCE - (percentStandby / 100) * RADIAL_CIRCUMFERENCE;
  const overdueStrokeOffset = RADIAL_CIRCUMFERENCE - (percentOverdue / 100) * RADIAL_CIRCUMFERENCE;

  const statusPages = useMemo(() => paginateRows(statusRows, maxPageRecords), [statusRows, maxPageRecords]);
  const statusRowsById = useMemo(() => {
    const map = new Map();
    statusRows.forEach((row) => {
      if (row?.id) {
        map.set(String(row.id), row);
      }
    });
    return map;
  }, [statusRows]);
  const downtimeRowsByDepartment = useMemo(() => {
    const map = new Map();
    dashboardDepartments.forEach((department) => {
      const departmentName = String(department).trim().toLowerCase();
      const rows = eventRows.filter((row) => {
        const rowDepartment = String(row.assetDepartment || '').trim().toLowerCase();
        return rowDepartment === departmentName;
      });
      map.set(department, rows);
    });
    return map;
  }, [dashboardDepartments, eventRows]);
  const plannedRows = useMemo(() => {
    return plannedEventRows;
  }, [plannedEventRows]);

  const utilisationRowsByDepartment = useMemo(() => {
    const map = new Map();
    dashboardDepartments.forEach((department) => {
      const departmentName = String(department).trim().toLowerCase();
      const rows = assetKpis.filter((row) => {
        const rowDepartment = String(row.assetDepartment || '').trim().toLowerCase();
        return rowDepartment === departmentName;
      });
      map.set(department, rows);
    });
    return map;
  }, [dashboardDepartments, assetKpis]);

  const assetCardRows = useMemo(() => {
    return assetKpis.map((row) => {
      const statusRow = statusRowsById.get(String(row.assetId));
      const statusKey = statusRow?.statusKey || 'up';
      const assetLabel = statusRow?.asset || row.asset || '—';
      const assetDescription =
        statusRow?.asset?.split(' - ').slice(1).join(' - ') || row.assetDescription;
      return {
        ...row,
        assetLabel,
        assetNumber: statusRow?.assetNumber || null,
        assetDescription,
        assetImage: row.assetImage || statusRow?.assetImage || null,
        statusKey,
      };
    });
  }, [assetKpis, statusRowsById]);

  const currentDowntimeDepartment =
    dashboardDepartments[downtimeDepartmentIndex] ?? dashboardDepartments[0] ?? null;
  const currentUtilisationDepartment =
    dashboardDepartments[utilisationDepartmentIndex] ?? dashboardDepartments[0] ?? null;
  const downtimePages = useMemo(() => {
    if (!currentDowntimeDepartment) return [];
    return paginateRows(downtimeRowsByDepartment.get(currentDowntimeDepartment) || [], maxPageRecords);
  }, [currentDowntimeDepartment, downtimeRowsByDepartment, maxPageRecords]);

  const utilisationPages = useMemo(() => {
    if (!currentUtilisationDepartment) return [];
    return paginateRows(utilisationRowsByDepartment.get(currentUtilisationDepartment) || [], maxPageRecords);
  }, [currentUtilisationDepartment, utilisationRowsByDepartment, maxPageRecords]);

  const assetCardPages = useMemo(() => {
    return paginateRows(assetCardRows || [], maxPageRecords);
  }, [assetCardRows, maxPageRecords]);
  const plannedPages = useMemo(() => {
    return paginateRows(plannedRows || [], maxPageRecords);
  }, [plannedRows, maxPageRecords]);

  const paginatedStatusRows = statusPages[statusPageIndex] || [];
  const paginatedDowntimeRows = downtimePages[downtimePageIndex] || [];
  const paginatedUtilisationRows = utilisationPages[utilisationPageIndex] || [];
  const paginatedAssetCardRows = assetCardPages[assetCardPageIndex] || [];
  const paginatedPlannedRows = plannedPages[plannedPageIndex] || [];

  const downtimeTotals = useMemo(() => {
    const rows = downtimeRowsByDepartment.get(currentDowntimeDepartment) || [];
    const planned = rows.filter((row) => row.eventTypeName?.toLowerCase() === 'planned').length;
    const breakdown = rows.filter((row) => row.eventTypeName?.toLowerCase() === 'breakdown').length;
    const standby = rows.filter((row) => row.eventTypeName?.toLowerCase() === 'standby').length;
    const overdue = rows.filter((row) => new Date(row.estimatedUp) < timestamp).length;
    const departmentAssets = eligibleAssets.filter((asset) => {
      const assetDepartment = String(asset?.assetDepartment?.name || '').trim().toLowerCase();
      return assetDepartment === String(currentDowntimeDepartment || '').trim().toLowerCase();
    });
    const totalAssets = departmentAssets.length;
    return {
      totalAssets,
      totalDown: rows.length - standby,
      planned,
      breakdown,
      standby,
      overdue,
    };
  }, [downtimeRowsByDepartment, currentDowntimeDepartment, eligibleAssets, timestamp]);
  const allAssetDowntimeTotals = useMemo(() => {
    return {
      totalAssets: eligibleAssets.length,
      totalDown: eventRows.length - standbyCount,
      planned: plannedCount,
      breakdown: breakdownCount,
      standby: standbyCount,
      overdue: overdueCount,
    };
  }, [
    breakdownCount,
    eligibleAssets.length,
    eventRows.length,
    overdueCount,
    plannedCount,
    standbyCount,
  ]);

  const utilisationSummary = useMemo(() => {
    const departmentMetrics = departmentSummary[currentUtilisationDepartment];
    if (departmentMetrics) {
      return {
        utilisation: departmentMetrics.effectiveUtilisation?.value ?? null,
        calendarUtilisation: departmentMetrics.calendarUtilisation?.value ?? null,
        mechanicalAvailability: departmentMetrics.mechanicalAvailability?.value ?? null,
        calendarAvailability: departmentMetrics.calendarAvailability?.value ?? null,
        stoppages: departmentMetrics.stoppageCountTotal?.value ?? 0,
        breakdownStoppages: departmentMetrics.stoppageCountBreakdown?.value ?? 0,
        mtbf: departmentMetrics.meanTimeBetweenFailures?.value ?? null,
        mttr: departmentMetrics.meanTimeToRepair?.value ?? null,
      };
    }

    const rows = utilisationRowsByDepartment.get(currentUtilisationDepartment) || [];
    const totals = rows.reduce(
      (acc, row) => {
        const usage = Number(row.usage) || 0;
        const calendarHours = Number(row.calendarHours) || 0;
        const mechanicalAvailability = Number(row.mechanicalAvailability);
        const calendarAvailability = Number(row.calendarAvailability);
        acc.totalUsage += usage;
        acc.totalCalendarHours += calendarHours;
        acc.totalMechanicalAvailability += mechanicalAvailability || 0;
        acc.mechanicalAvailabilityCount += Number.isFinite(mechanicalAvailability) ? 1 : 0;
        acc.totalCalendarAvailability += calendarAvailability || 0;
        acc.calendarAvailabilityCount += Number.isFinite(calendarAvailability) ? 1 : 0;
        acc.totalStoppages += Number(row.countAllEvents) || 0;
        acc.totalBreakdownStoppages += Number(row.countBreakdownEvents) || 0;
        acc.totalMtbf += Number(row.mtbf) || 0;
        acc.mtbfCount += Number.isFinite(Number(row.mtbf)) ? 1 : 0;
        acc.totalMttr += Number(row.mttr) || 0;
        acc.mttrCount += Number.isFinite(Number(row.mttr)) ? 1 : 0;
        return acc;
      },
      {
        totalUsage: 0,
        totalCalendarHours: 0,
        totalMechanicalAvailability: 0,
        mechanicalAvailabilityCount: 0,
        totalCalendarAvailability: 0,
        calendarAvailabilityCount: 0,
        totalStoppages: 0,
        totalBreakdownStoppages: 0,
        totalMtbf: 0,
        mtbfCount: 0,
        totalMttr: 0,
        mttrCount: 0,
      }
    );
    const calendarUtil = totals.totalCalendarHours
      ? (totals.totalUsage / totals.totalCalendarHours) * 100
      : null;
    const utilisation = totals.totalCalendarHours
      ? (totals.totalUsage / totals.totalCalendarHours) * 100
      : null;
    const mechanicalAvailability = totals.mechanicalAvailabilityCount
      ? totals.totalMechanicalAvailability / totals.mechanicalAvailabilityCount
      : null;
    const calendarAvailability = totals.calendarAvailabilityCount
      ? totals.totalCalendarAvailability / totals.calendarAvailabilityCount
      : null;
    const mtbf = totals.mtbfCount ? totals.totalMtbf / totals.mtbfCount : null;
    const mttr = totals.mttrCount ? totals.totalMttr / totals.mttrCount : null;
    return {
      utilisation,
      calendarUtilisation: calendarUtil,
      mechanicalAvailability,
      calendarAvailability,
      stoppages: totals.totalStoppages,
      breakdownStoppages: totals.totalBreakdownStoppages,
      mtbf,
      mttr,
    };
  }, [
    utilisationRowsByDepartment,
    currentUtilisationDepartment,
    departmentSummary,
  ]);

  useEffect(() => {
    if (isPaused) {
      return;
    }

    const timer = setTimeout(() => {
      if (screenIndex === 0) {
        if (statusPages.length && statusPageIndex < statusPages.length - 1) {
          setStatusPageIndex(prev => prev + 1);
          return;
        }
        setStatusPageIndex(0);
        setScreenIndex(1);
        setDowntimeDepartmentIndex(0);
        setDowntimePageIndex(0);
        return;
      }

      if (screenIndex === 1) {
        if (downtimePages.length && downtimePageIndex < downtimePages.length - 1) {
          setDowntimePageIndex(prev => prev + 1);
          return;
        }
        if (downtimeDepartmentIndex < dashboardDepartments.length - 1) {
          setDowntimeDepartmentIndex(prev => prev + 1);
          setDowntimePageIndex(0);
          return;
        }
        setDowntimeDepartmentIndex(0);
        setDowntimePageIndex(0);
        setScreenIndex(2);
        setUtilisationDepartmentIndex(0);
        setUtilisationPageIndex(0);
        return;
      }

      if (screenIndex === 2) {
        if (utilisationPages.length && utilisationPageIndex < utilisationPages.length - 1) {
          setUtilisationPageIndex(prev => prev + 1);
          return;
        }
        if (utilisationDepartmentIndex < dashboardDepartments.length - 1) {
          setUtilisationDepartmentIndex(prev => prev + 1);
          setUtilisationPageIndex(0);
          return;
        }
        setUtilisationDepartmentIndex(0);
        setUtilisationPageIndex(0);
        setScreenIndex(3);
        // setAssetCardDepartmentIndex(0);
        setAssetCardPageIndex(0);
        return;
      }

      if (screenIndex === 3) {
        if (assetCardPages.length && assetCardPageIndex < assetCardPages.length - 1) {
          setAssetCardPageIndex(prev => prev + 1);
          return;
        }
        //  if (assetCardDepartmentIndex < dashboardDepartments.length - 1) {
        // setAssetCardDepartmentIndex(prev => prev + 1);
        //  setAssetCardPageIndex(0);
        // return;

        // setAssetCardDepartmentIndex(0);
        setAssetCardPageIndex(0);
        setScreenIndex(4);
        setPlannedPageIndex(0);
        return;
      }

      if (screenIndex === 4) {
        if (plannedPages.length && plannedPageIndex < plannedPages.length - 1) {
          setPlannedPageIndex(prev => prev + 1);
          return;
        }
        setPlannedPageIndex(0);
        setScreenIndex(0);
      }
    }, REFRESH_INTERVAL_MS);

    return () => clearTimeout(timer);
  }, [
    screenIndex,
    statusPages.length,
    statusPageIndex,
    downtimePages.length,
    downtimePageIndex,
    downtimeDepartmentIndex,
    utilisationPages.length,
    utilisationPageIndex,
    utilisationDepartmentIndex,
    assetCardPages.length,
    assetCardPageIndex,
    plannedPages.length,
    plannedPageIndex,
    // assetCardDepartmentIndex,
    dashboardDepartments.length,
    REFRESH_INTERVAL_MS,
    isPaused,
  ]);

  useEffect(() => {
    if (!onTitleChange) return;

    if (screenIndex === 1) {
      onTitleChange(
        `Currently Down ${currentDowntimeDepartment ? ` - ${currentDowntimeDepartment}` : ''}`
      );
      return;
    }

    if (screenIndex === 2) {
      onTitleChange(
        `Utilisation and Reliability ${currentUtilisationDepartment ? ` - ${currentUtilisationDepartment}` : ''}`
      );
      return;
    }

    if (screenIndex === 3) {
      onTitleChange('Asset Performance Cards');
      return;
    }

    if (screenIndex === 4) {
      onTitleChange('Planned Events');
      return;
    }

    onTitleChange('Operating Status - All Assets');
  }, [
    screenIndex,
    currentDowntimeDepartment,
    currentUtilisationDepartment,
    onTitleChange,
  ]);

  if (!canAccess) {
    return <Navigate to="/access-denied" replace />;
  }

  return (
    <div className='card h-100'>

      <div className='card-body px-24 pt-0 pb-16'>
        {error && (
          <div className='alert alert-danger rounded-0 m-0'>{error}</div>
        )}
        {metricsError && (
          <div className='alert alert-danger rounded-0 mt-16'>{metricsError}</div>
        )}
        {screenIndex === 0 && (
          <>
            <div className='row g-16 mb-24'>
              <div className='col-12 col-md-3 col-lg-2'>
                <div className='card-body px-36 py-0 h-95 d-flex flex-column justify-content-center border rounded-3'>
                  <div className='d-flex justify-content-center'>
                    <div className="position-relative">
                      <svg className="radial-progress" data-percentage="90" viewBox="0 0 80 80">
                        <circle className="incomplete" cx="40" cy="40" r="35"></circle>
                        <circle className="complete radial-progress-secondary" cx="40" cy="40" r="35" style={{ strokeDashoffset: `${assetStrokeOffset}px` }}></circle>
                        <text className="percentage" x="50%" y="57%" transform="matrix(0, 1, -1, 0, 80, 0)"></text>
                      </svg>
                      <div className="text-center position-absolute top-50 start-50 translate-middle">
                        <span className="fw-semibold text-primary-light text-md lh-sm mt-8">Total Assets</span>
                        <h4 className="mb-0">{assetSummary.totalAssets}</h4>
                        <span className="text-secondary-light text-sm m-0">{percentInservice}% In Service</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className='col-12 col-md-3 col-lg-2'>
                <div className='card-body px-36 py-0 h-95 d-flex flex-column justify-content-center border rounded-3'>
                  <div className='d-flex justify-content-center'>
                    <div className="position-relative">
                      <svg className="radial-progress" data-percentage="90" viewBox="0 0 80 80">
                        <circle className="incomplete" cx="40" cy="40" r="35"></circle>
                        <circle className="complete" cx="40" cy="40" r="35" style={{ strokeDashoffset: `${standbyStrokeOffset}px` }}></circle>
                        <text className="percentage" x="50%" y="57%" transform="matrix(0, 1, -1, 0, 80, 0)"></text>
                      </svg>
                      <div className="text-center position-absolute top-50 start-50 translate-middle">
                        <span className="fw-semibold text-primary-light text-md lh-sm mt-8">Stand By</span>
                        <h4 className="mb-0">{standbyCount}</h4>
                        <span className="text-secondary-light text-sm m-0">{percentStandby}% of Assets</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className='col-12 col-md-3 col-lg-2'>
                <div className='card-body px-36 py-0 h-95 d-flex flex-column justify-content-center border rounded-3'>
                  <div className='d-flex justify-content-center'>
                    <div className="position-relative">
                      <svg className="radial-progress" data-percentage="90" viewBox="0 0 80 80">
                        <circle className="incomplete" cx="40" cy="40" r="35"></circle>
                        <circle className="complete radial-progress-warning" cx="40" cy="40" r="35" style={{ strokeDashoffset: `${downStrokeOffset}px` }}></circle>
                        <text className="percentage" x="50%" y="57%" transform="matrix(0, 1, -1, 0, 80, 0)"></text>
                      </svg>
                      <div className="text-center position-absolute top-50 start-50 translate-middle">
                        <span className="fw-semibold text-primary-light text-md lh-sm mt-8">Down</span>
                        <h4 className="mb-0">{totalDownCount}</h4>
                        <span className="text-secondary-light text-sm m-0">{percentDown}% of Assets</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className='col-12 col-md-3 col-lg-2'>
                <div className='card-body px-36 py-0 h-95 d-flex flex-column justify-content-center border rounded-3'>
                  <div className='d-flex justify-content-center'>
                    <div className="position-relative">
                      <svg className="radial-progress" data-percentage="90" viewBox="0 0 80 80">
                        <circle className="incomplete" cx="40" cy="40" r="35"></circle>
                        <circle className="complete radial-progress-success" cx="40" cy="40" r="35" style={{ strokeDashoffset: `${plannedStrokeOffset}px` }}></circle>
                        <text className="percentage" x="50%" y="57%" transform="matrix(0, 1, -1, 0, 80, 0)"></text>
                      </svg>
                      <div className="text-center position-absolute top-50 start-50 translate-middle">
                        <span className="fw-semibold text-primary-light text-md lh-sm mt-8">Planned</span>
                        <h4 className="mb-0">{plannedCount}</h4>
                        <span className="text-secondary-light text-sm m-0">{percentPlanned}% of Events</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className='col-12 col-md-3 col-lg-2'>
                <div className='card-body px-36 py-0 h-95 d-flex flex-column justify-content-center border rounded-3'>
                  <div className='d-flex justify-content-center'>
                    <div className="position-relative">
                      <svg className="radial-progress" data-percentage="90" viewBox="0 0 80 80">
                        <circle className="incomplete" cx="40" cy="40" r="35"></circle>
                        <circle className="complete radial-progress-danger" cx="40" cy="40" r="35" style={{ strokeDashoffset: `${breakStrokeOffset}px` }}></circle>
                        <text className="percentage" x="50%" y="57%" transform="matrix(0, 1, -1, 0, 80, 0)"></text>
                      </svg>
                      <div className="text-center position-absolute top-50 start-50 translate-middle">
                        <span className="fw-semibold text-primary-light text-md lh-sm mt-8">Breakdowns</span>
                        <h4 className="mb-0">{breakdownCount}</h4>
                        <span className="text-secondary-light text-sm m-0">{percentBreakdown}% of Events</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className='col-12 col-md-3 col-lg-2'>
                <div className='card-body px-36 py-0 h-95 d-flex flex-column justify-content-center border rounded-3'>
                  <div className='d-flex justify-content-center'>
                    <div className="position-relative">
                      <svg className="radial-progress" data-percentage="90" viewBox="0 0 80 80">
                        <circle className="incomplete" cx="40" cy="40" r="35"></circle>
                        <circle className="complete radial-progress-warning" cx="40" cy="40" r="35" style={{ strokeDashoffset: `${overdueStrokeOffset}px` }}></circle>
                        <text className="percentage" x="50%" y="57%" transform="matrix(0, 1, -1, 0, 80, 0)"></text>
                      </svg>
                      <div className="text-center position-absolute top-50 start-50 translate-middle">
                        <span className="fw-semibold text-primary-light text-md lh-sm mt-8">Overdue</span>
                        <h4 className="mb-0">{overdueCount}</h4>
                        <span className="text-secondary-light text-sm m-0">{percentOverdue}% of Events</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {planthireEnabled && (
                <div className='col-12 col-md-4'>
                  <div className='card-body px-36 py-0 h-100 d-flex flex-column border rounded-3'>
                    <h3 className="text-xl mt-16 mb-24">Hire Fleet Status</h3>
                    <div className="d-flex align-items-center justify-content-between gap-3 mb-16 pb-2">
                      <div className="d-flex align-items-center w-200-px">
                        <div className="flex-grow-1">
                          <h6 className="text-lg mb-0">On Hire</h6>
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2 w-100">
                        <div className="w-100 max-w-90 ms-auto">
                          <div className="progress rounded-pill" role="progressbar" aria-label="On Hire" aria-valuenow="25" aria-valuemin="0" aria-valuemax="100">
                            <div className="progress-bar bg-success-600 rounded-pill" style={{ width: `${percentOnhire}%` }}></div>
                          </div>
                        </div>
                        <span className="text-secondary-light font-xs fw-semibold w-40-px text-xl text-end">{assetSummary.onHire.toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="d-flex align-items-center justify-content-between gap-3 mb-16 pb-2">
                      <div className="d-flex align-items-center w-200-px">
                        <div className="flex-grow-1">
                          <h6 className="text-lg mb-0">Off Hire</h6>
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2 w-100">
                        <div className="w-100 max-w-90 ms-auto">
                          <div className="progress rounded-pill" role="progressbar" aria-label="Off Hire" aria-valuenow="25" aria-valuemin="0" aria-valuemax="100">
                            <div className="progress-bar bg-warning-600 rounded-pill" style={{ width: `${percentOffhire}%` }}></div>
                          </div>
                        </div>
                        <span className="text-secondary-light font-xs fw-semibold w-40-px text-xl text-end">{assetSummary.offHire.toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="d-flex align-items-center justify-content-between gap-3 mb-16 pb-2">
                      <div className="d-flex align-items-center w-200-px">
                        <div className="flex-grow-1">
                          <h6 className="text-lg mb-0">Non Hire</h6>
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2 w-100">
                        <div className="w-100 max-w-90 ms-auto">
                          <div className="progress rounded-pill" role="progressbar" aria-label="Other Assets" aria-valuenow="25" aria-valuemin="0" aria-valuemax="100">
                            <div className="progress-bar bg-secondary-600 rounded-pill" style={{ width: `${percentNonhire}%` }}></div>
                          </div>
                        </div>
                        <span className="text-secondary-light font-xs fw-semibold w-40-px text-xl text-end">{nonHire.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className='table-responsive'>
              <table className='table bordered-table mb-0 text-lg'>
                <thead>
                  <tr>
                    <th className="text-lg" scope='col'>Asset</th>
                    <th className="text-lg" scope='col'>Site</th>
                    <th className="text-lg" scope='col'>Time Down</th>
                    <th className="text-lg" scope='col'>Estimated Up</th>
                    <th className="text-lg" scope='col'>Down Hours</th>
                    <th className="text-lg" scope='col'>Reason</th>
                    <th className="text-lg" scope='col'>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {!loading && !assetLoading && statusRows.length === 0 && (
                    <tr>
                      <td colSpan='7' className='text-center py-4'>No assets available for the selected departments.</td>
                    </tr>
                  )}
                  {(loading || assetLoading) && (
                    <tr>
                      <td colSpan='7' className='text-center py-4'>
                        <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' /> Loading...
                      </td>
                    </tr>
                  )}
                  {!loading && !assetLoading &&
                    paginatedStatusRows.map((row) => {
                      const imageUrl = row.assetImage
                        ? `${process.env.REACT_APP_SPACES_BASE}/${row.assetImage}`
                        : placeholder;
                      const statusClass = STATUS_ROW_CLASS[row.statusKey] || STATUS_ROW_CLASS.up;

                      return (
                        <tr key={row.id}>
                          <td className={`py-2 ${statusClass}`}>
                            <div className='d-flex align-items-center gap-36'>
                              <div
                                className='rounded-circle overflow-hidden flex-shrink-0 border border-secondary-200'
                                style={{ width: '42px', height: '42px' }}
                              >
                                <img
                                  src={imageUrl}
                                  alt={row.asset}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              </div>
                              <span>{row.asset}</span>
                            </div>
                          </td>
                          <td className={`py-6 ${statusClass}`}> {`${row.assetDepartment || '—'} ${row.customer ? `(${row.customer})` : ""}`}</td>
                          <td className={`py-6 ${statusClass}`}>{row.timeDown}</td>
                          <td className={`py-6 ${statusClass} ${new Date(row.estimatedUp) < new Date() ? "text-danger" : ""}`}>
                            {row.estimatedUp}
                          </td>
                          <td className={`py-6 ${statusClass}`}>{row.downHours}</td>
                          <td className={`py-6 ${statusClass}`}>{row.reason}</td>
                          <td className={`py-6 ${statusClass}`}>
                            <span className={`text-md badge bg-${row.badgeColor}-focus text-${row.badgeColor} px-12 py-8 fw-semibold`}>{row.eventTypeName}</span>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </>
        )}
        {screenIndex === 1 && (
          <>
            <div className='row g-16 mb-24'>
              {[
                { label: 'Total Assets', value: downtimeTotals.totalAssets, percent: 100, color: 'secondary' },
                {
                  label: 'Down',
                  value: downtimeTotals.totalDown,
                  percent: calculatePercent(downtimeTotals.totalDown, downtimeTotals.totalAssets),
                  color: 'primary',
                },
                {
                  label: 'Stand By',
                  value: downtimeTotals.standby,
                  percent: calculatePercent(downtimeTotals.standby, downtimeTotals.totalAssets),
                  color: 'warning',
                },
                {
                  label: 'Planned',
                  value: downtimeTotals.planned,
                  percent: calculatePercent(downtimeTotals.planned, downtimeTotals.totalDown),
                  color: 'success',
                },
                {
                  label: 'Breakdowns',
                  value: downtimeTotals.breakdown,
                  percent: calculatePercent(downtimeTotals.breakdown, downtimeTotals.totalDown),
                  color: 'danger',
                },
                {
                  label: 'Overdue',
                  value: downtimeTotals.overdue,
                  percent: calculatePercent(downtimeTotals.overdue, downtimeTotals.totalDown),
                  color: 'warning',
                },
              ].map((metric) => (
                <div className='col-12 col-md-3 col-lg-2' key={metric.label}>
                  <div className='card-body px-36 py-0 h-95 d-flex flex-column justify-content-center border rounded-3'>
                    <div className='d-flex justify-content-center'>
                      <div className="position-relative">
                        <svg className="radial-progress" data-percentage="90" viewBox="0 0 80 80">
                          <circle className="incomplete" cx="40" cy="40" r="35"></circle>
                          <circle
                            className={`complete radial-progress-${metric.color}`}
                            cx="40"
                            cy="40"
                            r="35"
                            style={{
                              strokeDashoffset: `${RADIAL_CIRCUMFERENCE - (metric.percent / 100) * RADIAL_CIRCUMFERENCE}px`,
                            }}
                          ></circle>
                          <text className="percentage" x="50%" y="57%" transform="matrix(0, 1, -1, 0, 80, 0)"></text>
                        </svg>
                        <div className="text-center position-absolute top-50 start-50 translate-middle">
                          <span className="fw-semibold text-primary-light text-md lh-sm mt-8">{metric.label}</span>
                          <h4 className="mb-0">{metric.value}</h4>
                          <span className="text-secondary-light text-sm m-0">{metric.percent}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className='table-responsive'>
              <table className='table bordered-table mb-0 text-lg'>
                <thead>
                  <tr>
                    <th className="text-lg" scope='col'>Asset</th>
                    <th className="text-lg" scope='col'>Site</th>
                    <th className="text-lg" scope='col'>Time Down</th>
                    <th className="text-lg" scope='col'>Estimated Up</th>
                    <th className="text-lg" scope='col'>Down Hours</th>
                    <th className="text-lg" scope='col'>Reason</th>
                    <th className="text-lg" scope='col'>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {!loading && paginatedDowntimeRows.length === 0 && (
                    <tr>
                      <td colSpan='7' className='text-center py-4'>No downtime events in progress.</td>
                    </tr>
                  )}
                  {loading && (
                    <tr>
                      <td colSpan='7' className='text-center py-4'>
                        <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' /> Loading...
                      </td>
                    </tr>
                  )}
                  {!loading &&
                    paginatedDowntimeRows.map((row) => {
                      const imageUrl = row.assetImage
                        ? `${process.env.REACT_APP_SPACES_BASE}/${row.assetImage}`
                        : placeholder;

                      return (
                        <tr key={row.id}>
                          <td className="py-2">
                            <div className='d-flex align-items-center gap-36'>
                              <div
                                className='rounded-circle overflow-hidden flex-shrink-0 border border-secondary-200'
                                style={{ width: '42px', height: '42px' }}
                              >
                                <img
                                  src={imageUrl}
                                  alt={row.asset}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              </div>
                              <span>{row.asset}</span>
                            </div>
                          </td>
                          <td className="py-6"> {`${row.assetDepartment} ${row.customer ? `(${row.customer})` : ""}`}</td>
                          <td className="py-6">{row.timeDown}</td>
                          <td className={`py-6 ${new Date(row.estimatedUp) < new Date() ? "text-danger" : ""}`}>
                            {row.estimatedUp}
                          </td>
                          <td className="py-6">{row.downHours}</td>
                          <td className="py-6">{row.reason}</td>
                          <td className="py-6">
                            <span className={`text-md badge bg-${row.badgeColor}-focus text-${row.badgeColor} px-12 py-8 fw-semibold`}>{row.eventTypeName}</span>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </>
        )}
        {screenIndex === 2 && (
          <>
            <div className='row g-16 mb-24'>
              {[
                {
                  label: 'Calendar Utilisation',
                  value: utilisationSummary.calendarUtilisation,
                  unit: '%',
                  color: 'secondary',
                  percent: Number(utilisationSummary.calendarUtilisation),
                },
                {
                  label: 'Calendar Availability',
                  value: utilisationSummary.calendarAvailability,
                  unit: '%',
                  color: 'warning',
                  percent: Number(utilisationSummary.calendarAvailability),
                },
                {
                  label: 'Mechanical Availability',
                  value: utilisationSummary.mechanicalAvailability,
                  unit: '%',
                  color: 'success',
                  percent: Number(utilisationSummary.mechanicalAvailability),
                },
                { label: 'Stoppages', value: utilisationSummary.stoppages, unit: '', color: 'danger' },
                { label: 'MTBF', value: utilisationSummary.mtbf, unit: 'hrs', color: 'secondary' },
                { label: 'MTTR', value: utilisationSummary.mttr, unit: 'hrs', color: 'primary' },
              ].map((metric) => {
                const percent = Number.isFinite(metric.percent)
                  ? Math.min(Math.max(metric.percent, 0), 100)
                  : 100;
                return (
                  <div className='col-12 col-md-3 col-lg-2' key={metric.label}>
                    <div className='card-body px-36 py-0 h-95 d-flex flex-column justify-content-center border rounded-3'
>
                      <div className='d-flex justify-content-center'>
                        <div className="position-relative">
                          <svg className="radial-progress" data-percentage="90" viewBox="0 0 80 80">
                            <circle className="incomplete" cx="40" cy="40" r="35"></circle>
                            <circle
                              className={`complete radial-progress-${metric.color}`}
                              cx="40"
                              cy="40"
                              r="35"
                              style={{ strokeDashoffset: `${RADIAL_CIRCUMFERENCE - (percent / 100) * RADIAL_CIRCUMFERENCE}px` }}
                            ></circle>
                            <text className="percentage" x="50%" y="57%" transform="matrix(0, 1, -1, 0, 80, 0)"></text>
                          </svg>
                          <div className="text-center position-absolute top-50 start-50 translate-middle">
                            <span className="fw-semibold text-primary-light text-md lh-sm mt-8">{metric.label}</span>
                            <h4 className="mb-0">
                              {metric.unit === '%' ? formatNumber(metric.value, 1) : formatNumber(metric.value, 1)}
                            </h4>
                            <span className="text-secondary-light text-sm m-0">{metric.unit}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className='table-responsive'>
              <table className='table bordered-table mb-0 text-lg'>
                <thead>
                  <tr>
                    <th className="text-lg" scope='col'>Asset</th>
                    <th className="text-lg" scope='col'>Utilisation</th>
                    {/*<th className="text-lg" scope='col'>Calendar Utilisation</th>*/}
                    <th className="text-lg" scope='col'>Mechanical Availability</th>
                    <th className="text-lg" scope='col'>Calendar Availability</th>
                    <th className="text-lg" scope='col'>Stoppages</th>
                    <th className="text-lg" scope='col'>Breakdowns</th>
                    <th className="text-lg" scope='col'>MTBF</th>
                    <th className="text-lg" scope='col'>MTTR</th>
                  </tr>
                </thead>
                <tbody>
                  {!kpiLoading && paginatedUtilisationRows.length === 0 && (
                    <tr>
                      <td colSpan='9' className='text-center py-4'>No KPI data available for this department.</td>
                    </tr>
                  )}
                  {kpiLoading && (
                    <tr>
                      <td colSpan='9' className='text-center py-4'>
                        <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' /> Loading...
                      </td>
                    </tr>
                  )}
                  {!kpiLoading &&
                    paginatedUtilisationRows.map((row, index) => {
                      const imageUrl = row.assetImage
                        ? `${process.env.REACT_APP_SPACES_BASE}/${row.assetImage}`
                        : placeholder;

                      return (
                        <tr key={`${row.asset}-${index}`}>
                          <td className="py-2">
                            <div className='d-flex align-items-center gap-36'>
                              <div
                                className='rounded-circle overflow-hidden flex-shrink-0 border border-secondary-200'
                                style={{ width: '42px', height: '42px' }}
                              >
                                <img
                                  src={imageUrl}
                                  alt={row.asset}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              </div>
                              <span>{row.asset}</span>
                            </div>
                          </td>
                          <td className="py-6">{formatNumber(row.effectiveUtilisation, 1)}%</td>
                          {/*<td className="py-6">{formatNumber(row.calendarUtilisation, 1)}%</td>*/}
                          <td className="py-6">{formatNumber(row.mechanicalAvailability, 1)}%</td>
                          <td className="py-6">{formatNumber(row.calendarAvailability, 1)}%</td>
                          <td className="py-6">{row.countAllEvents ?? 0}</td>
                          <td className="py-6">{row.countBreakdownEvents ?? 0}</td>
                          <td className="py-6">{formatNumber(row.mtbf, 0)}</td>
                          <td className="py-6">{formatNumber(row.mttr, 1)}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </>
        )}
        {screenIndex === 3 && (
          <>
            <div className='row g-16 mb-24'>
              {[
                { label: 'Total Assets', value: allAssetDowntimeTotals.totalAssets, percent: 100, color: 'secondary' },
                {
                  label: 'Down',
                  value: allAssetDowntimeTotals.totalDown,
                  percent: calculatePercent(allAssetDowntimeTotals.totalDown, allAssetDowntimeTotals.totalAssets),
                  color: 'primary',
                },
                {
                  label: 'Stand By',
                  value: allAssetDowntimeTotals.standby,
                  percent: calculatePercent(allAssetDowntimeTotals.standby, allAssetDowntimeTotals.totalAssets),
                  color: 'warning',
                },
                {
                  label: 'Planned',
                  value: allAssetDowntimeTotals.planned,
                  percent: calculatePercent(allAssetDowntimeTotals.planned, allAssetDowntimeTotals.totalDown),
                  color: 'success',
                },
                {
                  label: 'Breakdowns',
                  value: allAssetDowntimeTotals.breakdown,
                  percent: calculatePercent(allAssetDowntimeTotals.breakdown, allAssetDowntimeTotals.totalDown),
                  color: 'danger',
                },
                {
                  label: 'Overdue',
                  value: allAssetDowntimeTotals.overdue,
                  percent: calculatePercent(allAssetDowntimeTotals.overdue, allAssetDowntimeTotals.totalDown),
                  color: 'warning',
                },
              ].map((metric) => (
                <div className='col-12 col-md-3 col-lg-2' key={metric.label}>
                  <div className='card-body px-36 py-0 h-95 d-flex flex-column justify-content-center border rounded-3'>
                    <div className='d-flex justify-content-center'>
                      <div className="position-relative">
                        <svg className="radial-progress" data-percentage="90" viewBox="0 0 80 80">
                          <circle className="incomplete" cx="40" cy="40" r="35"></circle>
                          <circle
                            className={`complete radial-progress-${metric.color}`}
                            cx="40"
                            cy="40"
                            r="35"
                            style={{
                              strokeDashoffset: `${RADIAL_CIRCUMFERENCE - (metric.percent / 100) * RADIAL_CIRCUMFERENCE}px`,
                            }}
                          ></circle>
                          <text className="percentage" x="50%" y="57%" transform="matrix(0, 1, -1, 0, 80, 0)"></text>
                        </svg>
                        <div className="text-center position-absolute top-50 start-50 translate-middle">
                          <span className="fw-semibold text-primary-light text-md lh-sm mt-8">{metric.label}</span>
                          <h4 className="mb-0">{metric.value}</h4>
                          <span className="text-secondary-light text-sm m-0">{metric.percent}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className='row g-16'>
              {!kpiLoading && paginatedAssetCardRows.length === 0 && (
                <div className='col-12'>
                  <div className='text-center py-4'>No KPI data available for this department.</div>
                </div>
              )}
              {kpiLoading && (
                <div className='col-12'>
                  <div className='text-center py-4'>
                    <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' /> Loading...
                  </div>
                </div>
              )}
              {!kpiLoading &&
                paginatedAssetCardRows.map((row) => {
                  const imageUrl = row.assetImage
                    ? `${process.env.REACT_APP_SPACES_BASE}/${row.assetImage}`
                    : placeholder;
                  const statusConfig = STATUS_CARD_CONFIG[row.statusKey] || STATUS_CARD_CONFIG.up;

                  return (
                    <div className='col-sm-12 col-lg-3 col-xl-4 col-xxl-3 col-xxxl-2 mb-24' key={row.assetId}>
                      <div className={`card h-100 border rounded-3 ${statusConfig.cardClass}`}>
                        <div className='card-body d-flex flex-column gap-16'>
                          <div className='d-flex align-items-start justify-content-between gap-12'>
                            <div className='d-flex align-items-center gap-12'>
                              <div
                                className='rounded-circle overflow-hidden flex-shrink-0 border border-secondary-200 bg-white'
                                style={{ width: '72px', height: '72px' }}
                              >
                                <img
                                  src={imageUrl}
                                  alt={row.assetLabel}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              </div>
                              <div>
                                <h4 className='mb-0 text-lg'>{row.assetNumber}</h4>
                                <span className='text-secondary-light'>{row.assetDescription || '—'}</span>
                              </div>
                            </div>
                            <div className='d-flex flex-column align-items-center gap-4'>
                              <div
                                className={`rounded-circle ${statusConfig.circleClass}`}
                                style={{ width: '36px', height: '36px' }}
                                title={statusConfig.label}
                              />
                              {/*<span className='text-md text-success'>{statusConfig.label}</span>*/}
                            </div>
                          </div>
                          <div className='d-flex flex-wrap justify-content-between gap-12'>
                            <div>
                              <span className='text-secondary-light'>Availability</span>
                              <h4 className='mb-4 text-xl'>
                                {formatNumber(row.calendarAvailability, 1)}%
                              </h4>
                            </div>
                            <div>
                              <span className='text-secondary-light'>MTBF</span>
                              <h4 className='mb-0 text-lg'>{formatNumber(row.mtbf, 0)}</h4>
                            </div>
                            <div>
                              <span className='text-secondary-light'>MTTR</span>
                              <h4 className='mb-0 text-lg'>{formatNumber(row.mttr, 1)}</h4>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </>
        )}
        {screenIndex === 4 && (
          <>
            <div className='row g-16 mb-24'>
              {[
                { label: 'Total Assets', value: allAssetDowntimeTotals.totalAssets, percent: 100, color: 'secondary' },
                {
                  label: 'Down',
                  value: allAssetDowntimeTotals.totalDown,
                  percent: calculatePercent(allAssetDowntimeTotals.totalDown, allAssetDowntimeTotals.totalAssets),
                  color: 'primary',
                },
                {
                  label: 'Stand By',
                  value: allAssetDowntimeTotals.standby,
                  percent: calculatePercent(allAssetDowntimeTotals.standby, allAssetDowntimeTotals.totalAssets),
                  color: 'warning',
                },
                {
                  label: 'Planned',
                  value: allAssetDowntimeTotals.planned,
                  percent: calculatePercent(allAssetDowntimeTotals.planned, allAssetDowntimeTotals.totalDown),
                  color: 'success',
                },
                {
                  label: 'Breakdowns',
                  value: allAssetDowntimeTotals.breakdown,
                  percent: calculatePercent(allAssetDowntimeTotals.breakdown, allAssetDowntimeTotals.totalDown),
                  color: 'danger',
                },
                {
                  label: 'Overdue',
                  value: allAssetDowntimeTotals.overdue,
                  percent: calculatePercent(allAssetDowntimeTotals.overdue, allAssetDowntimeTotals.totalDown),
                  color: 'warning',
                },
              ].map((metric) => (
                <div className='col-12 col-md-3 col-lg-2' key={metric.label}>
                  <div className='card-body px-36 py-0 h-95 d-flex flex-column justify-content-center border rounded-3'>
                    <div className='d-flex justify-content-center'>
                      <div className="position-relative">
                        <svg className="radial-progress" data-percentage="90" viewBox="0 0 80 80">
                          <circle className="incomplete" cx="40" cy="40" r="35"></circle>
                          <circle
                            className={`complete radial-progress-${metric.color}`}
                            cx="40"
                            cy="40"
                            r="35"
                            style={{
                              strokeDashoffset: `${RADIAL_CIRCUMFERENCE - (metric.percent / 100) * RADIAL_CIRCUMFERENCE}px`,
                            }}
                          ></circle>
                          <text className="percentage" x="50%" y="57%" transform="matrix(0, 1, -1, 0, 80, 0)"></text>
                        </svg>
                        <div className="text-center position-absolute top-50 start-50 translate-middle">
                          <span className="fw-semibold text-primary-light text-md lh-sm mt-8">{metric.label}</span>
                          <h4 className="mb-0">{metric.value}</h4>
                          <span className="text-secondary-light text-sm m-0">{metric.percent}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className='table-responsive'>
              <table className='table bordered-table mb-0 text-lg'>
                <thead>
                  <tr>
                    <th className="text-lg" scope='col'>Asset</th>
                    <th className="text-lg" scope='col'>Department Name</th>
                    <th className="text-lg" scope='col'>Planned Start</th>
                    <th className="text-lg" scope='col'>Planned End</th>
                    <th className="text-lg" scope='col'>Event Duration (hrs)</th>
                    <th className="text-lg" scope='col'>Location</th>
                  </tr>
                </thead>
                <tbody>
                  {!pendingLoading && paginatedPlannedRows.length === 0 && (
                    <tr>
                      <td colSpan='6' className='text-center py-4'>No planned events pending.</td>
                    </tr>
                  )}
                  {pendingLoading && (
                    <tr>
                      <td colSpan='6' className='text-center py-4'>
                        <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' /> Loading...
                      </td>
                    </tr>
                  )}
                  {!pendingLoading &&
                    paginatedPlannedRows.map((row) => {
                      const imageUrl = row.assetImage
                        ? `${process.env.REACT_APP_SPACES_BASE}/${row.assetImage}`
                        : placeholder;

                      return (
                        <tr key={row.id}>
                          <td className="py-2">
                            <div className='d-flex align-items-center gap-36'>
                              <div
                                className='rounded-circle overflow-hidden flex-shrink-0 border border-secondary-200'
                                style={{ width: '42px', height: '42px' }}
                              >
                                <img
                                  src={imageUrl}
                                  alt={row.assetLabel}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              </div>
                              <div className='d-flex flex-column'>
                                <span className='fw-semibold'>{row.assetNumber}</span>
                                <span className='text-secondary-light text-sm'>{row.assetDescription || '—'}</span>
                              </div>
                            </div>
                          </td>
                          <td className="py-6">{row.departmentName}</td>
                          <td className="py-6">{row.plannedStartDate}</td>
                          <td className="py-6">{row.plannedEndDate}</td>
                          <td className="py-6">{formatNumber(row.durationHours, 1)}</td>
                          <td className="py-6">{row.locationName}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PerformanceDashboardLayer;
